import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { DEFAULT_LOCALE, type Locale, SUPPORTED_LOCALES } from '@musicville/shared';
import { en } from './locales/en';

export type Dict = Record<string, string>;

const LOADERS: Record<Exclude<Locale, 'en'>, () => Promise<Dict>> = {
  az: () => import('./locales/az').then((m) => m.az),
  ru: () => import('./locales/ru').then((m) => m.ru),
};

const loaded: Partial<Record<Locale, Dict>> = { en };

const STORAGE_KEY = 'mv.locale';

export type TVars = Record<string, string | number>;

interface I18nValue {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: (key: string, vars?: TVars) => string;
  locales: readonly Locale[];
}

const I18nContext = createContext<I18nValue | null>(null);

export function I18nProvider({ children }: { children: ReactNode }): React.JSX.Element {
  const [locale, setLocaleState] = useState<Locale>(() => readInitialLocale());
  const [, onDictionaryLoaded] = useState(0);

  const dict = loaded[locale] ?? en;

  const setLocale = useCallback((next: Locale) => {
    setLocaleState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {}
  }, []);

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  useEffect(() => {
    if (loaded[locale]) return;

    let cancelled = false;
    void LOADERS[locale as Exclude<Locale, 'en'>]()
      .then((fetched) => {
        loaded[locale] = fetched;
        if (!cancelled) onDictionaryLoaded((n) => n + 1);
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [locale]);

  const value = useMemo<I18nValue>(
    () => ({
      locale,
      setLocale,
      locales: SUPPORTED_LOCALES,
      t: (key: string, vars?: TVars) => interpolate(lookup(dict, key, locale, vars), vars),
    }),
    [locale, setLocale, dict],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nValue {
  const value = useContext(I18nContext);
  if (!value) throw new Error('useI18n must be used inside <I18nProvider>');
  return value;
}

export function useT(): (key: string, vars?: TVars) => string {
  return useI18n().t;
}

function lookup(dict: Dict, key: string, locale: Locale, vars?: TVars): string {
  if (vars && typeof vars['count'] === 'number') {
    const category = pluralRules(locale).select(vars['count']);
    const candidates = [`${key}.${category}`, `${key}.other`, key];

    for (const candidate of candidates) {
      if (dict[candidate] !== undefined) return dict[candidate];
    }
    for (const candidate of candidates) {
      if (en[candidate] !== undefined) return en[candidate];
    }
    return key;
  }
  return dict[key] ?? en[key] ?? key;
}

const pluralCache = new Map<Locale, Intl.PluralRules>();

function pluralRules(locale: Locale): Intl.PluralRules {
  let rules = pluralCache.get(locale);
  if (!rules) {
    rules = new Intl.PluralRules(intlLocaleTag(locale));
    pluralCache.set(locale, rules);
  }
  return rules;
}

function intlLocaleTag(locale: Locale): string {
  switch (locale) {
    case 'az':
      return 'az-AZ';
    case 'ru':
      return 'ru-RU';
    default:
      return 'en-GB';
  }
}

function interpolate(template: string, vars?: TVars): string {
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, (whole, name: string) =>
    name in vars ? String(vars[name]) : whole,
  );
}

function readInitialLocale(): Locale {
  try {
    const stored = localStorage.getItem(STORAGE_KEY) as Locale | null;
    if (stored && SUPPORTED_LOCALES.includes(stored)) return stored;
  } catch {}

  const preferred = navigator.languages?.find((lang) =>
    SUPPORTED_LOCALES.includes(lang.slice(0, 2) as Locale),
  );
  return (preferred?.slice(0, 2) as Locale) ?? DEFAULT_LOCALE;
}

export const LOCALE_LABELS: Record<Locale, string> = { az: 'AZ', en: 'ENG', ru: 'RUS' };
